import { useState } from "react";
import {
    useFlyerTypes,
    useDeleteFlyerType,
    type FlyerType,
} from "../api/flyerTypes";
import { FlyerTypeManagementForm } from "./FlyerTypeManagementForm";
import { showError, confirmAction } from "../shared/toast";
import "./flyer-types-panel.css";

export const FlyerTypesManagementPage = (): JSX.Element => {
    const [editingFlyerType, setEditingFlyerType] = useState<FlyerType | null>(null);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const { data: flyerTypes = [], isLoading, error } = useFlyerTypes();
    const deleteMutation = useDeleteFlyerType();

    const handleEdit = (flyerType: FlyerType) => {
        setEditingFlyerType(flyerType);
        setShowCreateForm(false);
    };

    const handleDelete = async (id: string, name: string) => {
        const confirmed = await confirmAction(
            `Är du säker på att du vill ta bort flygbladet "${name}"? Detta kan påverka kampanjer som använder det.`
        );
        if (!confirmed) {
            return;
        }

        try {
            await deleteMutation.mutateAsync(id);
        } catch (error: any) {
            const message =
                error?.response?.data?.message ||
                "Kunde inte ta bort flygbladet. Kontrollera att det inte används i kampanjer.";
            showError("Kunde inte ta bort flygbladet", message);
        }
    };

    const handleFormSuccess = () => {
        setEditingFlyerType(null);
        setShowCreateForm(false);
    };

    const handleFormCancel = () => {
        setEditingFlyerType(null);
        setShowCreateForm(false);
    };

    if (error) {
        return (
            <div className="flyer-types-panel">
                <h2>Flygblad</h2>
                <p className="error-message">Kunde inte ladda flygblad.</p>
            </div>
        );
    }

    return (
        <section className="flyer-types-panel" aria-label="Flygblad">
            <div className="flyer-types-panel__header">
                <h2>Flygblad</h2>
                <p className="flyer-types-panel__description">
                    Hantera flygblad som kan användas i kampanjer. Skapa nya eller redigera befintliga.
                </p>
                <button
                    className="button button--primary"
                    onClick={() => {
                        setShowCreateForm(true);
                        setEditingFlyerType(null);
                    }}
                    aria-label="Skapa nytt flygblad"
                >
                    + Skapa nytt flygblad
                </button>
            </div>

            {(showCreateForm || editingFlyerType) && (
                <div className="flyer-types-panel__form">
                    <FlyerTypeManagementForm
                        flyerType={editingFlyerType || undefined}
                        onSuccess={handleFormSuccess}
                        onCancel={handleFormCancel}
                    />
                </div>
            )}

            {isLoading ? (
                <p>Laddar...</p>
            ) : (
                <div className="flyer-types-panel__list">
                    {flyerTypes.length === 0 ? (
                        <p>Inga flygblad hittades. Skapa ditt första flygblad ovan.</p>
                    ) : (
                        <table className="flyer-types-table">
                            <thead>
                                <tr>
                                    <th>Namn</th>
                                    <th>Beskrivning</th>
                                    <th>Bild</th>
                                    <th>Åtgärder</th>
                                </tr>
                            </thead>
                            <tbody>
                                {flyerTypes.map((ft) => (
                                    <tr key={ft.id}>
                                        <td>
                                            <strong>{ft.name}</strong>
                                        </td>
                                        <td>{ft.summary || "-"}</td>
                                        <td>
                                            {ft.imageUrl ? (
                                                <img
                                                    src={ft.imageUrl}
                                                    alt={ft.name}
                                                    style={{ maxWidth: "50px", maxHeight: "50px", objectFit: "contain" }}
                                                />
                                            ) : (
                                                "-"
                                            )}
                                        </td>
                                        <td>
                                            <div className="flyer-types-table__actions">
                                                <button
                                                    className="button button--secondary"
                                                    onClick={() => handleEdit(ft)}
                                                    aria-label={`Redigera ${ft.name}`}
                                                >
                                                    Redigera
                                                </button>
                                                <button
                                                    className="button button--danger"
                                                    onClick={() => handleDelete(ft.id, ft.name)}
                                                    disabled={deleteMutation.isPending}
                                                    aria-label={`Ta bort ${ft.name}`}
                                                >
                                                    {deleteMutation.isPending ? "Tar bort..." : "Ta bort"}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
        </section>
    );
};